/**
 * useChallengesQuery - Hook para dados de desafios
 * 
 * Encapsula chamadas ao challengesService com cache do React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { getCachedUser } from '../../services/userCacheService';
import { getActiveChallengesOptimized } from '../../services/challengesService';

// ==================== QUERY KEYS ====================
export const challengesKeys = {
    all: ['challenges'],
    list: () => [...challengesKeys.all, 'list'],
    active: () => [...challengesKeys.all, 'active'],
    activeOptimized: (userId) => [...challengesKeys.all, 'activeOptimized', userId ?? null],
    detail: (id) => [...challengesKeys.all, 'detail', id],
    participants: (id) => [...challengesKeys.all, 'participants', id],
    userChallenges: (userId) => [...challengesKeys.all, 'user', userId],
};

// ==================== FETCH FUNCTIONS ====================

/**
 * Busca todos os desafios ativos
 */
const fetchActiveChallenges = async () => {
    const { data, error } = await supabase
        .from('challenges')
        .select(`
            id,
            titulo,
            descricao,
            tipo,
            disciplina,
            assunto,
            meta_questoes,
            premio_xp,
            data_inicio,
            data_fim,
            ativo,
            icone,
            criado_por,
            gradient_colors,
            created_at,
            participants:challenge_participants(count)
        `)
        .eq('ativo', true)
        .gte('data_fim', new Date().toISOString())
        .order('data_fim', { ascending: true });

    if (error) throw error;
    return data || [];
};

/**
 * Busca detalhes de um desafio específico
 */
const fetchChallengeById = async (challengeId) => {
    const { data, error } = await supabase
        .from('challenges')
        .select(`
            *,
            participants:challenge_participants(
                id,
                user_id,
                score,
                questoes_respondidas,
                questoes_corretas,
                joined_at,
                profiles!challenge_participants_user_id_fkey(id, nome, foto_url, nivel)
            )
        `)
        .eq('id', challengeId)
        .single();

    if (error) throw error;
    return data;
};

/**
 * Busca participantes de um desafio
 */
const fetchChallengeParticipants = async (challengeId) => {
    const { data, error } = await supabase
        .from('challenge_participants')
        .select(`
            id,
            challenge_id,
            user_id,
            score,
            questoes_respondidas,
            questoes_corretas,
            joined_at,
            profiles!challenge_participants_user_id_fkey(id, nome, foto_url, nivel)
        `)
        .eq('challenge_id', challengeId)
        .order('score', { ascending: false });

    if (error) throw error;
    return data || [];
};

/**
 * Busca desafios do usuário
 */
const fetchUserChallenges = async (userId) => {
    const { data, error } = await supabase
        .from('challenge_participants')
        .select(`
            *,
            challenge:challenges(*)
        `)
        .eq('user_id', userId)
        .order('joined_at', { ascending: false });

    if (error) throw error;
    return data || [];
};

// ==================== HOOKS ====================

/**
 * Hook para buscar desafios ativos
 */
export const useActiveChallenges = (enabled = true) => {
    return useQuery({
        queryKey: challengesKeys.active(),
        queryFn: fetchActiveChallenges,
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled,
    });
};

/**
 * Hook para buscar detalhes de um desafio
 */
export const useChallenge = (challengeId, enabled = true) => {
    return useQuery({
        queryKey: challengesKeys.detail(challengeId),
        queryFn: () => fetchChallengeById(challengeId),
        staleTime: 2 * 60 * 1000, // 2 minutos
        enabled: enabled && !!challengeId,
    });
};

/**
 * Hook para buscar participantes de um desafio
 */
export const useChallengeParticipants = (challengeId, enabled = true) => {
    return useQuery({
        queryKey: challengesKeys.participants(challengeId),
        queryFn: () => fetchChallengeParticipants(challengeId),
        staleTime: 1 * 60 * 1000, // 1 minuto (participantes mudam frequentemente)
        enabled: enabled && !!challengeId,
    });
};

/**
 * Hook para buscar desafios do usuário
 */
export const useUserChallenges = (userId, enabled = true) => {
    return useQuery({
        queryKey: challengesKeys.userChallenges(userId),
        queryFn: () => fetchUserChallenges(userId),
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para desafios ativos no formato otimizado da ChallengesScreen
 * (inclui isParticipating). Mesmo shape de getActiveChallengesOptimized.
 */
export const useActiveChallengesOptimized = (userId, enabled = true) => {
    return useQuery({
        queryKey: challengesKeys.activeOptimized(userId),
        queryFn: () => getActiveChallengesOptimized(userId),
        staleTime: 5 * 60 * 1000,
        enabled,
    });
};

// ==================== MUTATIONS ====================

/**
 * Hook para participar de um desafio
 */
export const useJoinChallenge = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (challengeId) => {
            const user = await getCachedUser();
            if (!user) throw new Error('Usuário não autenticado');

            const { data, error } = await supabase
                .from('challenge_participants')
                .insert({
                    challenge_id: challengeId,
                    user_id: user.id,
                    score: 0,
                    questoes_respondidas: 0,
                    questoes_corretas: 0,
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        },
        onSuccess: (_, challengeId) => {
            queryClient.invalidateQueries({ queryKey: challengesKeys.detail(challengeId) });
            queryClient.invalidateQueries({ queryKey: challengesKeys.participants(challengeId) });
            queryClient.invalidateQueries({ queryKey: challengesKeys.active() });
        },
    });
};
